import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import "./NewEmployeeStyles.scss";
import { DrawerComponent } from "../../../../components/UI/Drawer/DrawerComponent";
import {
  clearNewEmployeeError,
  createEmployee,
  editEmployee,
  fetchDomains,
} from "../../../../slices/Admin/userManagement.slice";
import { showSuccess } from "../../../../helpers/Notifications/Notification";
import NewFormHeader from "../../../../components/Common/NewFormHeader";
import { Button } from "../../../../components/ds";
import FormFields, { validateEmployeeFields } from "./FormFields";

const initialFromEmployee = (employeeData) => ({
  name: employeeData?.name || "",
  department: employeeData?.department || "",
  incident_role: employeeData?.incident_role || "",
  employee_id: employeeData?.employee_id || "",
  email: employeeData?.email || "",
  domains_restricted_to: (employeeData?.domains || [])
    .map((d) => d?.id)
    .filter((id) => id != null),
});

const NewEmployee = ({
  drawerVisible,
  onClose,
  setDrawerVisible,
  populateEmployeeList,
  isEdit,
  employeeData,
  searchItem,
}) => {
  const dispatch = useDispatch();
  const { newEmployeeError, createEmployeeLoading, domainsList } = useSelector(
    (state) => state.userManagement
  );
  const { activeSurveyId } = useSelector((state) => state.employeeDashboard);
  const [values, setValues] = useState(() => initialFromEmployee(employeeData));
  const [errors, setErrors] = useState({});

  useEffect(() => {
    domainsList.length == 0 && dispatch(fetchDomains(1)); // crr = 1
  }, [activeSurveyId]);

  useEffect(() => {
    if (drawerVisible) {
      setValues(initialFromEmployee(employeeData));
      setErrors({});
    }
  }, [drawerVisible, employeeData, isEdit]);

  const setDrawerClose = () => {
    onClose();
    dispatch(clearNewEmployeeError());
  };

  const handleChange = (name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleBlurField = (name) => {
    const next = validateEmployeeFields(values, isEdit);
    if (next[name]) {
      setErrors((prev) => ({ ...prev, [name]: next[name] }));
    }
  };

  const onSubmit = (event) => {
    event.preventDefault();
    const nextErrors = validateEmployeeFields(values, isEdit);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    if (isEdit) {
      dispatch(
        editEmployee(
          {
            email: employeeData?.email,
            department: values?.department,
            incident_role: values?.incident_role || "",
            domains_restricted_to: values?.domains_restricted_to,
          },
          (status) => {
            if (status === "success") {
              showSuccess("Employee details edited");
              populateEmployeeList(searchItem);
              setDrawerVisible(false);
            }
          }
        )
      );
    } else {
      dispatch(
        createEmployee(values, (status) => {
          if (status === "success") {
            showSuccess("New Employee Added");
            populateEmployeeList(searchItem);
            setDrawerVisible(false);
          }
        })
      );
    }
  };

  return (
    <DrawerComponent
      content={
        <>
          <NewFormHeader
            title={`${isEdit ? "Edit" : "Add"} Employee`}
            description={"Fill the following fields to add an employee."}
            error={newEmployeeError}
          />
          <form onSubmit={onSubmit} autoComplete="off" noValidate>
            <FormFields
              isEdit={isEdit}
              values={values}
              errors={errors}
              onChange={handleChange}
              onBlurField={handleBlurField}
            />

            <div className="actions-container">
              <Button
                type="button"
                variant="secondary"
                onClick={setDrawerClose}
              >
                CANCEL
              </Button>
              <Button type="submit" disabled={createEmployeeLoading}>
                {createEmployeeLoading
                  ? isEdit
                    ? "UPDATING…"
                    : "ADDING…"
                  : isEdit
                    ? "UPDATE"
                    : "ADD"}
              </Button>
            </div>
          </form>
        </>
      }
      drawerVisible={drawerVisible}
      onClose={setDrawerClose}
    />
  );
};

export default NewEmployee;
